import { GxMarker, MapArtefact } from '../../../../utils/map/gx-map-utils';
import { Injectable } from '@angular/core';
import { BaseMapService } from '../../../global/map/base-map.service';
import { getHexCode } from '../../../../core/utilities';
import { getFittedBoundsAroundPoints } from '../../../../utils/map/turf-utils';
import { GmbDiffMarkerType } from '../../diff-utils';
import { Location } from '../../../../models/locations-models/location';
import { MAP_ID_STYLING } from '../map-utils';

export interface GmbDiffMarker extends GxMarker {
  meta: {
    uuid: string;
    markerType: GmbDiffMarkerType;
  } & GxMarker['meta'];
}

@Injectable()
export class BlsGmbDifferenceMapService extends BaseMapService {

  private map: google.maps.Map;
  private mapArtefacts: MapArtefact<GmbDiffMarker>[] = [];

  constructor() {
    super();
  }

  /**
   * Map instance setter to suit the differences-popup use case.
   * @param map
   */
  setMap(map: google.maps.Map) {
    this.flushMap();
    this.map = map;

    this.map.setOptions({
      mapTypeControlOptions: {
        mapTypeIds: [
          google.maps.MapTypeId.ROADMAP,
          // the hybrid mode shows the street names and POIs
          // in contrast to the satellite view which is quite useless here
          google.maps.MapTypeId.HYBRID,
        ],
        position: google.maps.ControlPosition.TOP_RIGHT,
      },

      // clicking cmd / ctrl is required to use scrollwheel to zoom in/out
      gestureHandling: 'cooperative',

      // no pegman at this point
      streetViewControl: false,

      // show POIs
      mapId: MAP_ID_STYLING,

      // the icons are disabled in the base map and since we're showing their icons,
      // let's make sure they're clickable
      clickableIcons: true,
    });

    // make chainable
    return this;
  }

  /**
   * BASE MAP SERVICE OVERRIDES
   * */

  /**
   * Extract the lat/long pair.
   * @param location
   * @protected
   */
  protected extractPosition(
      location: Location,
  ): google.maps.LatLngLiteral | null {
    return {
      // `coordinate` is the field of interest for both 'VERIFIED' and 'GMB' pins!
      lat: location.coordinate?.latitude,
      lng: location.coordinate?.longitude,
    };
  }

  /**
   * Override using the metadata `markerType`.
   * @param location
   * @param filteredAddressListLocations
   * @param metadata
   * @protected
   */
  protected prepareMarkerText(
      location: Location,
      filteredAddressListLocations?: Location[],
      metadata?: GmbDiffMarker['meta'],
  ): string | undefined {
    return metadata['markerType'];
  }

  /**
   * Override and always return `blue-40`.
   * @param location
   * @param metadata
   * @protected
   */
  protected prepareMarkerColor(
      location: Location,
      metadata?: GmbDiffMarker['meta'],
  ): string {
    return getHexCode('blue-40');
  }

  /**
   * RENDERING
   * */

  /**
   * Render the pins
   * @param locations
   */
  renderLocationMarkers(locations: Location[]) {
    for (const location of locations) {
      const uuid = location.uuid;
      const position = this.extractPosition(location);

      const metadata: GmbDiffMarker['meta'] = {
        uuid: uuid,
        markerType: uuid as GmbDiffMarkerType,
        positionOverride: position,
      };

      const marker = this.generateMarker<GmbDiffMarker>(
          location,
          metadata.id,
          this.map,
          [],
          metadata,
      );

      this.mapArtefacts.push({
        parentLocation: {
          id: marker.meta.id as string,
          marker: marker,
          eventListeners: [],
        },
      });
    }

    this.fitMap();
  }

  /**
   * Due to the async nature of maps, let's have real-time info about the fittable bounds
   * @private
   */
  private getFittedBounds() {
    return getFittedBoundsAroundPoints(
        {
          positions: this.mapArtefacts.map(artefact => artefact.parentLocation.marker.getPosition().toJSON()),
        },
    );
  }

  /**
   * Fit the map based on the contents of `mapArtefacts`.
   * If there are some artefacts, buffer a rect around those points.
   * If not, fit Europe by buffering its center w/ 1500km in each cardinal direction.
   * @private
   */
  private fitMap() {
    const bounds = this.getFittedBounds();

    // the map tiles are presumed to be loaded -- fit the bounds straightaway
    this.map.fitBounds(bounds);
  }

  /**
   * Public wrapper
   */
  getMapArtefacts(): MapArtefact<GmbDiffMarker>[] {
    return this.mapArtefacts;
  }


  /**
   * ON DESTROY
   * */

  flushMap() {
    this.mapArtefacts = BlsGmbDifferenceMapService.flushMapArtefacts(this.mapArtefacts, null);
  }
}